import React, { useState, useEffect } from 'react';
import { Button, Row, Col, Card, Form } from 'react-bootstrap';
import OrderProductsForm from './OrderProductsForm';
import DeleteConfirmationModal from '../Global/DeleteConfirmationModal';
import { getFurnitureTypes } from '../../api';
import '../Global/Style/OrderProductListForm.css';

const OrderProductListForm = ({ setSelectedFurnitureType, isDirty, setIsDirty, onProductsCompletionChange, onMileageChange, onTotalPriceChange, initialProducts = [] }) => {
    const [products, setProducts] = useState(initialProducts);
    const [mileage, setMileage] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [productsCompletionStatus, setProductsCompletionStatus] = useState({});
    const [totalPrice, setTotalPrice] = useState(0);
    const [furnitureTypes, setFurnitureTypes] = useState([]);

    useEffect(() => {
        getFurnitureTypes(false).then(setFurnitureTypes).catch(console.error);
    }, []);

    const handleMileageChange = (e) => {
        const value = e.target.value;
        if (!isNaN(value) && value >= 0) {
            setMileage(value);
            setIsDirty(true); // Indicar que hubo un cambio en el formulario
            onMileageChange(value);
        }
    };

    useEffect(() => {
        const calculatedMileage = (Number(mileage) * 1.2 - 40);
        const productTotal = Array.isArray(products)
            ? products.reduce((sum, product) => sum + (product.price || 0) * (product.quantity || 1), 0)
            : 0;

        const total = productTotal + (calculatedMileage > 0 ? calculatedMileage : 0);
        setTotalPrice(total);

        if (onTotalPriceChange) {
            onTotalPriceChange(Number(total.toFixed(2)));
        }
    }, [products, mileage, onTotalPriceChange]);

    const handleAddProduct = () => {
        const newProduct = { id: Date.now(), price: 0 };
        setProducts((prevProducts) => [...prevProducts, newProduct]);
        setIsDirty(true);
    };

    const handleShowDeleteModal = (product) => {
        setProductToDelete(product);
        setShowDeleteModal(true);
    };

    const handleDeleteProduct = () => {
        if (productToDelete) {
            const updatedProducts = products.filter(
                (product) => product.id !== productToDelete.id
            );
            setProducts(updatedProducts);
            setProductsCompletionStatus((prevStatus) => {
                const { [productToDelete.id]: _, ...remainingStatus } = prevStatus;
                return remainingStatus;
            });
            setProductToDelete(null);
            setIsDirty(true);
        }
        setShowDeleteModal(false);
    };

    const handleCancelDelete = () => {
        setProductToDelete(null);
        setShowDeleteModal(false);
    };

    const handlePriceChange = (updatedProduct) => {
        setProducts((prevProducts) => {
            const existingProduct = prevProducts.find(product => product.id === updatedProduct.id);
            
            // Para productos especiales, asegurarse de que el precio no se resetee cuando se cambia la descripción
            if (updatedProduct.isSpecial && existingProduct && existingProduct.isSpecial) {
                // Si es un cambio de descripción y el precio es 0, mantener el precio anterior
                if (updatedProduct.price === 0 && existingProduct.price > 0 && 
                    updatedProduct.description !== existingProduct.description) {
                    updatedProduct.price = existingProduct.price;
                }
            }
            
            // Don't skip updates for special products
            if (!updatedProduct.isSpecial && existingProduct && 
                existingProduct.price === updatedProduct.price && 
                existingProduct.quantity === updatedProduct.quantity) {
                return prevProducts;
            }
            
            const updatedProducts = prevProducts.map((product) =>
                product.id === updatedProduct.id ? { ...product, ...updatedProduct } : product
            );
            
            setIsDirty(true);
            return updatedProducts;
        });
    };

    const handleFormCompletion = (productId, isComplete) => {
        setProductsCompletionStatus((prevStatus) => ({
            ...prevStatus,
            [productId]: isComplete,
        }));
    };

    useEffect(() => {
        if (Object.keys(productsCompletionStatus).length === 0 && products.length === 0) {
            onProductsCompletionChange(false);
            return;
        }

        // Separate products by type
        const specialProducts = products.filter(product => product.isSpecial === true);
        const normalProducts = products.filter(product => product.isSpecial !== true);
        
        // Always consider special products complete
        const specialProductsComplete = specialProducts.length === 0 || 
            specialProducts.every(product => product.description && product.description.trim() !== '' && !!product.productTypeId);
        
        // Check if normal products are complete using the completion status
        const normalProductIds = normalProducts.map(product => product.id);
        const normalProductsStatus = Object.entries(productsCompletionStatus)
            .filter(([id]) => normalProductIds.includes(Number(id)) || normalProductIds.includes(id))
            .map(([_, status]) => status);
        
        const normalProductsComplete = normalProducts.length === 0 || 
            (normalProductsStatus.length === normalProducts.length && 
             normalProductsStatus.every(status => status === true));
        
        // Both types must be complete
        const allComplete = specialProductsComplete && normalProductsComplete;
        
        onProductsCompletionChange(allComplete);
    }, [productsCompletionStatus, onProductsCompletionChange, products]);

    // Inicializar productos del lead
    useEffect(() => {
        if (initialProducts.length > 0 && products.length === 0) {
            setProducts(initialProducts);
        }
    }, [initialProducts]);

    useEffect(() => {
        if (products.length > 0) {
            setSelectedFurnitureType(products);
        }
    }, [products, setSelectedFurnitureType]);

    return (
        <div className="order-product-list-form" data-testid="order-product-list-form">
            <Row className="mb-4">
                <Col>
                    <h2 className="section-title" data-testid="products-section-title">Productos en el pedido</h2>
                </Col>
            </Row>

            <div className="product-list mt-4" data-testid="products-list">
                {products.map((product) => (
                    <Card key={product.id} className="product-card mb-3 w-100" data-testid={`product-card-${product.id}`}>
                        <Card.Body className="d-flex flex-column">
                            <Row className="flex-grow-1">
                                <Col>
                                    <OrderProductsForm
                                        product={product}
                                        onPriceChange={handlePriceChange}
                                        onFormCompletion={handleFormCompletion}
                                        furnitureTypes={furnitureTypes}
                                    />
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col xs={12}>
                                    <Button
                                        variant="danger"
                                        onClick={() => handleShowDeleteModal(product)}
                                        className="delete-btn w-100"
                                        data-testid={`delete-product-button-${product.id}`}
                                    >
                                        Eliminar Producto
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                ))}
            </div>

            <Row className="total-price-row" data-testid="total-price-row">
                <Col xs={12}>
                    { (
                        <div className="total-price-container" data-testid="total-price-container">
                            <span className="total-price-label">Total:</span>
                            <span className="total-price-value" data-testid="total-price-value">{totalPrice.toFixed(2)}€</span>
                        </div>
                    )}
                </Col>
            </Row>

            <DeleteConfirmationModal
                show={showDeleteModal}
                onHide={handleCancelDelete}
                onConfirm={handleDeleteProduct}
                message="¿Estás seguro de que deseas eliminar este producto?"
            />

            <Button variant="success" size="lg" onClick={handleAddProduct} className="mb-3 w-100" data-testid="add-product-button">
                <i className="fas fa-plus-circle"></i> Añadir Producto
            </Button>

            {/* Campo de Kilometraje */}
            <Card className="mb-4" data-testid="mileage-card">
                <Card.Body>
                    <Form.Group controlId="orderMileage">
                        <Form.Label>Kilometraje</Form.Label>
                        <Form.Control
                            type="text"
                            value={mileage}
                            onChange={handleMileageChange}
                            placeholder="Introduce el kilometraje, si aplica"
                            data-testid="mileage-input"
                        />
                    </Form.Group>
                </Card.Body>
            </Card>
        </div>
    );
};

export default OrderProductListForm;
